import { Injectable, inject, OnDestroy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

/**
 * Breakpoint constants matching SCSS _variables.scss
 * These should be kept in sync with the SCSS breakpoint values
 */
export const BREAKPOINTS = {
  /** Extra small screens (phones portrait) */
  XS: 480,
  /** Small screens (phones landscape) */
  SM: 640,
  /** Medium screens (tablets) - matches $breakpoint-md */
  MD: 768,
  /** Large screens (small desktops) - matches $breakpoint-lg */
  LG: 1024,
  /** Extra large screens (desktops) - matches $breakpoint-xl */
  XL: 1280,
  /** 2X large screens (large desktops) - matches $breakpoint-2xl */
  XXL: 1536,
} as const;

/**
 * Media query strings matching SCSS responsive mixins
 */
export const MEDIA_QUERIES = {
  /** Mobile: max-width 767px (matches @mixin mobile) */
  MOBILE: `(max-width: ${BREAKPOINTS.MD - 1}px)`,
  /** Tablet: 768px - 1023px (matches @mixin tablet) */
  TABLET: `(min-width: ${BREAKPOINTS.MD}px) and (max-width: ${BREAKPOINTS.LG - 1}px)`,
  /** Desktop: min-width 1024px (matches @mixin desktop) */
  DESKTOP: `(min-width: ${BREAKPOINTS.LG}px)`,
  /** Large desktop: min-width 1200px (matches @mixin large-desktop) */
  LARGE_DESKTOP: '(min-width: 1200px)',
  /** Handset portrait: uses Angular CDK Breakpoints */
  HANDSET_PORTRAIT: Breakpoints.HandsetPortrait,
} as const;

/**
 * Current breakpoint type
 */
export type BreakpointType = 'mobile' | 'tablet' | 'desktop';

/**
 * ResponsiveService provides reactive breakpoint detection for responsive UI.
 *
 * Uses Angular CDK BreakpointObserver to efficiently track viewport changes.
 * Breakpoint values match the SCSS variables defined in _variables.scss.
 *
 * @example
 * ```typescript
 * // In component using inject()
 * private responsive = inject(ResponsiveService);
 *
 * // Reactive usage in template
 * <div *ngIf="responsive.isMobile$ | async">Mobile content</div>
 *
 * // Synchronous check in component
 * if (this.responsive.isMobile) {
 *   // Mobile-specific logic
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ResponsiveService implements OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);

  // Cached values for synchronous access
  private _isMobile = false;
  private _isTablet = false;
  private _isDesktop = true;
  private _isHandset = false;
  private _currentBreakpoint: BreakpointType = 'desktop';

  // BehaviorSubjects for reactive streams with initial values
  private readonly _isMobile$ = new BehaviorSubject<boolean>(false);
  private readonly _isTablet$ = new BehaviorSubject<boolean>(false);
  private readonly _isDesktop$ = new BehaviorSubject<boolean>(true);
  private readonly _isHandset$ = new BehaviorSubject<boolean>(false);
  private readonly _currentBreakpoint$ = new BehaviorSubject<BreakpointType>('desktop');

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    this.initializeBreakpointObservers();
  }

  /**
   * Initialize all breakpoint observers
   */
  private initializeBreakpointObservers(): void {
    // Mobile observer (max-width: 767px)
    this.subscriptions.push(
      this.breakpointObserver
        .observe(MEDIA_QUERIES.MOBILE)
        .pipe(
          map((result) => result.matches),
          distinctUntilChanged(),
        )
        .subscribe((isMobile) => {
          this._isMobile = isMobile;
          this._isMobile$.next(isMobile);
          this.updateCurrentBreakpoint();
        }),
    );

    // Tablet observer (768px - 1023px)
    this.subscriptions.push(
      this.breakpointObserver
        .observe(MEDIA_QUERIES.TABLET)
        .pipe(
          map((result) => result.matches),
          distinctUntilChanged(),
        )
        .subscribe((isTablet) => {
          this._isTablet = isTablet;
          this._isTablet$.next(isTablet);
          this.updateCurrentBreakpoint();
        }),
    );

    // Desktop observer (min-width: 1024px)
    this.subscriptions.push(
      this.breakpointObserver
        .observe(MEDIA_QUERIES.DESKTOP)
        .pipe(
          map((result) => result.matches),
          distinctUntilChanged(),
        )
        .subscribe((isDesktop) => {
          this._isDesktop = isDesktop;
          this._isDesktop$.next(isDesktop);
          this.updateCurrentBreakpoint();
        }),
    );

    // Handset portrait observer (using Angular CDK built-in)
    this.subscriptions.push(
      this.breakpointObserver
        .observe(MEDIA_QUERIES.HANDSET_PORTRAIT)
        .pipe(
          map((result) => result.matches),
          distinctUntilChanged(),
        )
        .subscribe((isHandset) => {
          this._isHandset = isHandset;
          this._isHandset$.next(isHandset);
        }),
    );
  }

  /**
   * Update the current breakpoint based on active states
   */
  private updateCurrentBreakpoint(): void {
    let breakpoint: BreakpointType;

    if (this._isMobile) {
      breakpoint = 'mobile';
    } else if (this._isTablet) {
      breakpoint = 'tablet';
    } else {
      breakpoint = 'desktop';
    }

    if (this._currentBreakpoint !== breakpoint) {
      this._currentBreakpoint = breakpoint;
      this._currentBreakpoint$.next(breakpoint);
    }
  }

  // ---------------------------------------------------------------------------
  // Observable Streams
  // ---------------------------------------------------------------------------

  /**
   * Observable that emits true when viewport is mobile (max-width: 767px)
   */
  get isMobile$(): Observable<boolean> {
    return this._isMobile$.asObservable();
  }

  /**
   * Observable that emits true when viewport is tablet (768px - 1023px)
   */
  get isTablet$(): Observable<boolean> {
    return this._isTablet$.asObservable();
  }

  /**
   * Observable that emits true when viewport is desktop (min-width: 1024px)
   */
  get isDesktop$(): Observable<boolean> {
    return this._isDesktop$.asObservable();
  }

  /**
   * Observable that emits true when viewport is handset portrait
   * Uses Angular CDK Breakpoints.HandsetPortrait
   */
  get isHandset$(): Observable<boolean> {
    return this._isHandset$.asObservable();
  }

  /**
   * Observable that emits the current breakpoint type ('mobile' | 'tablet' | 'desktop')
   */
  get currentBreakpoint$(): Observable<BreakpointType> {
    return this._currentBreakpoint$.asObservable();
  }

  // ---------------------------------------------------------------------------
  // Synchronous Getters (cached values)
  // ---------------------------------------------------------------------------

  /**
   * Returns true if viewport is mobile (max-width: 767px)
   * Uses cached value for synchronous access
   */
  get isMobile(): boolean {
    return this._isMobile;
  }

  /**
   * Returns true if viewport is tablet (768px - 1023px)
   * Uses cached value for synchronous access
   */
  get isTablet(): boolean {
    return this._isTablet;
  }

  /**
   * Returns true if viewport is desktop (min-width: 1024px)
   * Uses cached value for synchronous access
   */
  get isDesktop(): boolean {
    return this._isDesktop;
  }

  /**
   * Returns true if viewport is handset portrait
   * Uses cached value for synchronous access
   */
  get isHandset(): boolean {
    return this._isHandset;
  }

  /**
   * Returns the current breakpoint type
   * Uses cached value for synchronous access
   */
  get currentBreakpoint(): BreakpointType {
    return this._currentBreakpoint;
  }

  // ---------------------------------------------------------------------------
  // Utility Methods
  // ---------------------------------------------------------------------------

  /**
   * Check if viewport matches a custom media query
   * @param query Media query string (e.g., '(min-width: 600px)')
   * @returns Observable that emits when the media query matches/unmatches
   */
  matchesQuery$(query: string): Observable<boolean> {
    return this.breakpointObserver.observe(query).pipe(
      map((result) => result.matches),
      distinctUntilChanged(),
    );
  }

  /**
   * Check if viewport currently matches a custom media query (synchronous)
   * @param query Media query string (e.g., '(min-width: 600px)')
   * @returns true if the media query currently matches
   */
  matchesQuery(query: string): boolean {
    return this.breakpointObserver.isMatched(query);
  }

  /**
   * Check if viewport is at least the specified breakpoint
   * @param breakpoint Minimum breakpoint width in pixels
   * @returns true if viewport width >= breakpoint
   */
  isAtLeast(breakpoint: number): boolean {
    return this.breakpointObserver.isMatched(`(min-width: ${breakpoint}px)`);
  }

  /**
   * Check if viewport is at most the specified breakpoint
   * @param breakpoint Maximum breakpoint width in pixels
   * @returns true if viewport width <= breakpoint
   */
  isAtMost(breakpoint: number): boolean {
    return this.breakpointObserver.isMatched(`(max-width: ${breakpoint}px)`);
  }

  /**
   * Check if viewport width is between two breakpoints (inclusive)
   * @param minBreakpoint Minimum width in pixels
   * @param maxBreakpoint Maximum width in pixels
   * @returns true if viewport is within range
   */
  isBetween(minBreakpoint: number, maxBreakpoint: number): boolean {
    return this.breakpointObserver.isMatched(
      `(min-width: ${minBreakpoint}px) and (max-width: ${maxBreakpoint}px)`,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this._isMobile$.complete();
    this._isTablet$.complete();
    this._isDesktop$.complete();
    this._isHandset$.complete();
    this._currentBreakpoint$.complete();
  }
}
